import { Box } from '@mui/material';
import React, { useState } from 'react';
import type FundOpportunity from '../../models/FundOpportunity';
import { SECONDARY_BUTTON_HOVER_SX, StyledButton } from '../styles/StyledButtons';
import UploadDocumentDialog from './UploadDocumentDialog';
import type { useFundUpdatesAdmin } from './useFundUpdatesAdmin';

/**
 * The two ways to start a new update: Add Update creates an empty draft straight away; Upload
 * Document opens its dialog instantly and only creates a draft once an import succeeds.
 * Either way the new draft is opened via `onCreated`.
 */
export default function CreateDraftRow({
  controller,
  fundOpportunity,
  onCreated,
}: {
  controller: ReturnType<typeof useFundUpdatesAdmin>;
  fundOpportunity: FundOpportunity;
  onCreated?: (id: string) => void;
}) {
  const { state, handleCreateDraft } = controller;
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  return (
    <Box sx={{ mt: '10px', mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <StyledButton
        noTopSpacing
        onClick={async () => {
          const created = await handleCreateDraft();
          if (created) onCreated?.(created.externalId);
        }}
        disabled={state.creatingDraft}
      >
        {state.creatingDraft ? 'Creating...' : 'Add Update'}
      </StyledButton>
      <StyledButton
        noTopSpacing
        buttonVariant="inactiveOrCancel"
        sx={SECONDARY_BUTTON_HOVER_SX}
        onClick={() => setUploadDialogOpen(true)}
      >
        Upload Document
      </StyledButton>
      <UploadDocumentDialog
        open={uploadDialogOpen}
        fundOpportunity={fundOpportunity}
        createDraft={handleCreateDraft}
        onClose={() => setUploadDialogOpen(false)}
        onImported={(id) => {
          setUploadDialogOpen(false);
          onCreated?.(id);
        }}
      />
    </Box>
  );
}
